/**
 * Engagement Module Interfaces
 * Shared types for quiz-task, image-task, and other engagement modules
 */

// Import existing master data interfaces
import type { Region } from '../models/master-data/region.interface';
import type { State } from '../models/master-data/state.interface';
import type { City } from '../models/master-data/city.interface';
import type { UserRole } from '../models/user-management/supervisor-split-assign.interface';

/**
 * Main Engagement Entity
 * Represents a quiz task or image task
 */
export interface Engagement {
  task_id: number; // API uses task_id instead of id
  engagement_type_id: number; // API uses engagement_type_id
  engagement_type_name?: string;
  engagement_name: string;
  engagement_description?: string;
  reward_type?: number;
  reward_type_name?: string;
  reward_points?: number;
  whitelist_type: number;
  whitelist_type_name?: string;
  zone_type?: number | null;
  zone_type_name?: string;
  zone_id?: string | null;
  role_type?: number;
  role_type_name?: string;
  start_date: string;
  end_date: string;
  status?: number;
  cd?: string; // Created date from API
  created_at?: string;
  updated_at?: string;
  portfolio_id?: number;
  portfolio?: string; // API uses 'portfolio' for name
  campaign_id?: number | null;

  // Quiz/Content & Learning specific fields
  course_description?: string; // HTML content for course description
  course_content?: string; // YouTube URL or content link
  quiz_configured?: number; // 1 = quiz is configured, 0 = not configured
  pre_expiry_date?: string; // Pre-expiry date for rewards
}

/**
 * Engagement Type
 * Used in dropdown for selecting engagement type
 */
export interface EngagementType {
  id: number;
  name: string;
  engagement_page: number;
}

/**
 * Quiz Question with Options
 * Used in quiz-task FormArray
 */
export interface QuizQuestion {
  question: string;
  answer: string;
  points: number;
  options: QuizOption[];
}

/**
 * Quiz Option
 * Each question can have up to 4 options
 */
export interface QuizOption {
  option: string;
}

/**
 * Whitelisted User
 * User assigned to an engagement
 */
export interface WhitelistUser {
  id: number;
  user_id: number;
  username: string; // API returns 'username', not 'user_name'
  mobile_number: string; // API returns 'mobile_number'
  email?: string;
  phone?: string;
  region_name?: string;
  state_name?: string;
  city_name?: string;
  status: number | null; // API can return null
  created_at?: string;
}

// =============================================================================
// API Request Interfaces
// =============================================================================

/**
 * Request: Get engagement types
 */
export interface GetEngagementTypesRequest {
  engagement_page: number; // 2 for quiz-task, 3 for image-task
}

/**
 * Request: Get engagements list with filters
 */
export interface GetEngagementsRequest {
  engagement_page?: number; // 2 for quiz-task, 3 for image-task
  program_id?: number;
  engagement_type?: number;
  portfolio_id?: number;
  start_date?: string;
  end_date?: string;
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Request: View engagement detail for editing
 * Used in edit mode to prepopulate form
 */
export interface ViewEngagementRequest {
  engagement_page: number; // 2 for quiz, 3 for image
  engagement_type_id: number;
  portfolio_id?: number;
  task_id: number;
}

/**
 * Request: Create/Update Quiz Engagement
 * Same endpoint for create and update, differentiated by isEdit flag
 */
export interface CreateEngagementContentRequest {
  portfolio_id: number;
  engagement_id: number; // engagement_type_id
  engagement_title: string;
  start_date: string; // YYYY-MM-DD format
  end_date: string; // YYYY-MM-DD format
  quiz_points_criteria: number; // reward_type
  questions: QuizQuestion[];
  isEdit: number; // 0 for create, 1 for update
  task_id?: number; // Present when updating
  course_type: number; // 4 for quiz
  audit_instructions: string;
  whitelist_type: number;
  zone_type?: number;
  role_type?: number;
}

/**
 * Request: Create/Update Quiz + Content Task (New API)
 * Used for Content & Learning module (engagement_page=3)
 */
export interface CreateQuizContentTaskRequest {
  program_id: string | number;
  portfolio_id: number;
  engagement_type_id: string | number;
  engagement_page: number; // 3 for content & learning
  engagement_title: string;
  start_date: string; // YYYY-MM-DD format
  end_date: string; // YYYY-MM-DD format
  course_type: string | number; // "3" for content
  course_description?: string; // HTML content
  course_content?: string; // URL (YouTube, etc.)
  quiz_configured: string | number; // "1" if quiz is configured
  quiz_points_criteria?: string | number; // reward type
  reward_points?: number | null;
  pre_expiry_date?: string; // YYYY-MM-DD format
  questions: QuizQuestion[];
  task_id?: string | number; // Present when updating
  isEdit: number; // 0 for create, 1 for update
  whitelist_type: string | number;
  zone_type?: string | number;
  role_type?: string | number;
}

/**
 * Request: Delete Question and Options
 */
export interface DeleteQuestionAndOptionsRequest {
  engagement_type_id: number;
  engagement_task_id: number;
  deletedQuestionId?: string | number;
  deletedOptionsId?: string | number;
}

/**
 * Request: Get whitelisted users
 */
export interface GetWhitelistUsersRequest {
  engagement_page: number; // 1 for image-task, 2 for quiz-task
  engagement_type: number; // engagement_type_id
  task_id: number;
  search?: string;
}

/**
 * Request: Whitelist users (zone-based or all) - Used by all engagement types
 */
export interface WhitelistEngagementRequest {
  engagement_type_id: string | number;
  task_id: string | number;
  mobile_numbers?: Array<{
    'Mobile Number': string;
    isEdit: boolean;
  }>; // Optional: Required for Excel upload, not needed for zone-based
  whitelist_type: number | string;
  zone_type?: number | string; // Optional: Required for zone-based uploads
  zone_id?: number | string | null; // Optional: Required for zone-based uploads
  program_id: string | number;
}

/**
 * Request: Disable/toggle engagement user status
 */
export interface DisableEngagementUserRequest {
  engagement_id: number;
  id: number; // whitelist user id
  status: number; // 0 or 1
  program_id: string | number;
}

/**
 * Request: Deactivate entire engagement
 */
export interface DeactivateEngagementRequest {
  engagement_id: string | number;
  id: number;
  program_id: string | number;
}

/**
 * Request: Assign users to engagement (whitelist)
 */
export interface AssignUsersRequest {
  program_id: number;
  engagement_id: number;
  user_ids: number[];
  upload_file?: File; // For Excel upload
}

/**
 * Request: Disable user from engagement
 */
export interface DisableUserRequest {
  engagement_id: number;
  user_id: number;
}

/**
 * Request: Get zones (region/state/city)
 */
export interface GetZonesRequest {
  program_id?: number;
  region_id?: number;
  state_id?: number;
}

// =============================================================================
// API Response Interfaces
// =============================================================================

/**
 * Response: Get engagement types
 */
export interface GetEngagementTypesResponse {
  data: {
    engagementtypes: EngagementType[];
    message?: string;
  };
  success: boolean;
  status: number;
}

/**
 * Response: Get engagements list
 */
export interface GetEngagementsResponse {
  data: {
    data: Engagement[]; // API returns data.data array
    pagination: {
      totalCount: number;
      pageSize: number;
      page: number;
      pageCount: number;
    };
  };
  success: boolean;
  status: number;
}

/**
 * Response: View engagement detail for editing
 */
export interface ViewEngagementResponse {
  data: {
    engagement: {
      engagement_type_id: number;
      engagement_title: string;
      start_date: string;
      end_date: string;
      quiz_points_criteria: number;
      course_description?: string; // HTML content
      course_content?: string; // URL
      quiz_configured?: number; // 1 if configured
      reward_points?: number;
      pre_expiry_date?: string;
      whitelist_type: number;
      zone_type?: number;
      role_type?: number;
      questions?: Array<{
        question: string;
        answer: string;
        points: number;
        option: Array<{
          option: string;
        }>;
      }>;
    };
    message?: string;
  };
  success: boolean;
}

/**
 * Response: Create/Update engagement
 */
export interface CreateEngagementResponse {
  body: {
    data: {
      task_id: number;
      sub_tasks_id: string;
      has_subtask: number;
      engagement_type_id: number;
      engagement_type: string;
      message?: string;
    };
  };
  status: number;
}

/**
 * Response: Get whitelisted users
 */
export interface GetWhitelistUsersResponse {
  body: {
    data: {
      data: WhitelistUser[];
      full_data?: WhitelistUser[]; // For excel export
    };
  };
  status: number;
}

/**
 * Response: Whitelist engagement users (zone-based or all)
 */
export interface WhitelistEngagementResponse {
  body: {
    data: {
      message: string;
    };
  };
  status: number;
}

/**
 * Response: Disable/toggle engagement user status
 */
export interface DisableEngagementResponse {
  data: {
    message: string;
  };
  status: number;
}

/**
 * Response: Assign users (for Excel upload)
 */
export interface AssignUsersResponse {
  data: {
    result: {
      assigned_count: number;
      message: string;
      rejected_records?: any[]; // For Excel upload errors
    };
    message?: string;
  };
  success: boolean;
}

/**
 * Response: Disable user
 */
export interface DisableUserResponse {
  data: {
    result: {
      message: string;
    };
    message?: string;
  };
  success: boolean;
}

/**
 * Response: Get user roles
 */
export interface GetUserRolesResponse {
  success: boolean;
  status: number;
  data: UserRole[];
}

/**
 * Response: Get regions
 */
export interface GetRegionsResponse {
  data: {
    result: Region[];
    message?: string;
  };
  success: boolean;
}

/**
 * Response: Get states
 */
export interface GetStatesResponse {
  data: {
    result: State[];
    message?: string;
  };
  success: boolean;
}

/**
 * Response: Get cities
 */
export interface GetCitiesResponse {
  data: {
    result: City[];
    message?: string;
  };
  success: boolean;
}

// =============================================================================
// Image Task Specific Interfaces
// =============================================================================

/**
 * Image Task Template Instructions
 * Stored as JSON string in template_instructions field
 */
export interface ImageTaskTemplateInstructions {
  templete_type: number; // 1 = single image, 2 = double image (typo from API)
  single_image?: string; // URL for single image type
  templete_wrong_img?: string; // URL for wrong image (double type)
  temple_right_img?: string; // URL for right image (double type)
  image_insruction?: string; // Instruction text for single image
  image_insruction_wrong?: string; // Instruction for wrong image
  image_insruction_right?: string; // Instruction for right image
  templete_insruction_one?: string; // General instruction 1
  templete_insruction_two?: string; // General instruction 2
  templete_insruction_three?: string; // General instruction 3
  templete_title?: string; // Template title
}

/**
 * Image Task Detail
 * Response from viewEngagementDetail API (engagement_page=1)
 */
export interface ImageTaskDetail {
  task_id: number;
  task_title: string;
  engagement_title: string; // Note: different from list (engagement_name)
  engagement_type_id: number;
  portfolio_id: number;
  start_date: string;
  end_date: string;
  reward_type: number; // 1 = customized, 2 = fixed
  points?: number; // For fixed reward type
  weekly_points?: number; // For customized reward
  half_month?: number; // For customized reward
  full_month?: number; // For customized reward
  posm_element_config_id?: number;
  audit_criteria: string; // CKEditor HTML content
  template_instructions: string; // JSON string of ImageTaskTemplateInstructions
  whitelist_type: number;
  zone_type?: number;
  role_type: number;
  is_whitelisted: boolean; // If users already whitelisted, disable some fields
  points_calculated?: number; // For sub-tasks
  has_subtask?: number;
  sub_tasks?: SubTask[];
}

/**
 * Sub Task (for customized reward type)
 */
export interface SubTask {
  id: number; // sub_task_id
  task_id: number;
  start_date: string;
  end_date: string;
}

/**
 * Request: Create/Update Image Engagement (Picture Task)
 */
export interface CreateImageEngagementRequest {
  task_title: string;
  portfolio_id: number;
  engagement_page: number; // 1 for picture task
  engagement_type_id: string | number;
  engagement_title: string;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  reward_type: string | number; // "2" = fixed
  image_insert_id?: number[]; // Array of element IDs
  full_month_points: number;
  element_id?: string | number;
  audit_criteria: string; // CKEditor content
  instructions: string;
  task_id?: string | number; // For edit mode
  description: string;
  success_msg: string;
  failure_msg: string;
  template_instructions: ImageTaskTemplateInstructions; // Sent as object, API stores as JSON
  isEdit: number; // 0 = create, 1 = update
  whitelist_type: string | number;
  zone_type: string | number;
  role_type: string | number;
  program_id: string | number;
}

/**
 * Response: Create Image Engagement
 */
export interface CreateImageEngagementResponse {
  body: {
    data: {
      task_id: number;
      sub_tasks_id: string;
      has_subtask: number;
      engagement_type_id: number;
      engagement_type: string;
      message?: string;
    };
  };
  status: number;
}

/**
 * Request: Upload Instruction Image
 * Uploads image and returns URL
 */
export interface UploadInstructionImageRequest {
  engagement_type_id: number; // ID from engagement types API
  element_id: number; // Same as engagement_type_id
  image_type: number; // 1 = right, 2 = wrong, 3 = single
  image: string; // Base64 encoded image
}

/**
 * Response: Upload Instruction Image
 */
export interface UploadInstructionImageResponse {
  body: {
    data: {
      image: string; // URL of uploaded image
    };
  };
  status: number;
}

/**
 * Response: Excel Upload Whitelist
 */
export interface ExcelUploadWhitelistResponse {
  body: {
    data: {
      success_count: number;
      error_count: number;
      errors: Array<{
        mobile_number: string;
        reason: string;
      }>;
      message?: string;
    };
  };
  status: number;
}
